
import type { PropType, SetupContext } from 'vue'
import type { LinkedListItem } from '../../models'

import { defineComponent, nextTick, onBeforeUnmount, onMounted, onUpdated, ref } from 'vue'

import { AlignEnum, ColorEnum } from '../../enums'

import { EpbText } from '../shared/epb-text'
import TransitionSize from '../transition-size.component.vue'

interface ItemCoordinatesInfo {
  x1: number
  y1: number
  x2: number
  y2: number
  height: number
  width: number
}

export default defineComponent({
  name: 'ItemLinkedList',
  components: {
    TransitionSize,
    EpbText,
  },
  props: {
    item: {
      type: Object as PropType<LinkedListItem>,
      required: true,
    },
    reversed: Boolean,
    collapsed: Boolean,
    isRoot: Boolean,
  },
  setup(
    props: {
      item: LinkedListItem
      reversed: boolean
      collapsed: boolean
      isRoot: boolean
    },
    ctx: SetupContext,
  ) {
    const { emit } = ctx

    const baseClass = 'epb-item-linked-list'
    const labelClass = `${baseClass}__label`
    const svgClass = `${baseClass}__svg`
    const pathClass = `${baseClass}__path`

    const hovered = ref(false)
    const redrawOnHoverInterval = ref(undefined)
    const redrawOnHoverTimeout = ref(undefined)
    const redrawOnClickInterval = ref(undefined)

    const hasChildren = (item: LinkedListItem): boolean => {
      return !!item.children?.length
    }

    const getExpandedIcon = (expanded: boolean): string => {
      return expanded ? 'collapse' : 'expand'
    }

    const getCoordinatesInfo = (element: Element): ItemCoordinatesInfo => {
      const boundingClientRect = element.getBoundingClientRect()
      return {
        x1: Math.round(boundingClientRect.left),
        y1: Math.round(boundingClientRect.top),
        x2: Math.round(boundingClientRect.right),
        y2: Math.round(boundingClientRect.bottom),
        height: Math.round(boundingClientRect.height),
        width: Math.round(boundingClientRect.width),
      }
    }

    const drawSvgPathLines = (): void => {
      const labels = Array.from(document.getElementsByClassName(labelClass)) as HTMLElement[]
      const svgs = Array.from(document.getElementsByClassName(svgClass)) as HTMLElement[]
      const paths = Array.from(document.getElementsByClassName(pathClass)) as HTMLElement[]

      labels.forEach((item, index) => {
        const nextItem = labels[index + 1]
        const svg = svgs[index]
        const path = paths[index]
        if (nextItem) {
          const coordinatesItem = getCoordinatesInfo(item)
          const coordinatesNextItem = getCoordinatesInfo(nextItem)
          const coordinatesSvg = getCoordinatesInfo(svg)

          const svgWidth = coordinatesSvg.width
          if (svgWidth === 60 && path.getAttribute('d') !== '') {
            return
          }

          const deltaXStart = coordinatesItem.x2 - coordinatesSvg.x1
          const deltaXEnd = coordinatesNextItem.x1 - coordinatesSvg.x2

          const deltaXMiddle1 = Math.round((svgWidth + deltaXStart + deltaXEnd) / 3)
          const deltaXMiddle2 = Math.round((svgWidth + deltaXStart + deltaXEnd) / 2)

          const deltaYStart = Math.round(
            coordinatesItem.y1 + coordinatesItem.height / 2 - coordinatesSvg.y1,
          )
          const deltaYEnd = Math.round(
            coordinatesNextItem.y1 + coordinatesNextItem.height / 2 - coordinatesSvg.y1,
          )

          const deltaYMiddle1 = deltaYStart + (deltaYEnd > deltaYStart ? -4 : 4)
          const deltaYMiddle2 = Math.round(deltaYEnd - (deltaYEnd - deltaYStart) / 2)

          const pathString =
            `M ${deltaXStart} ${deltaYStart} ` +
            `Q ${deltaXMiddle1} ${deltaYMiddle1 - 2} ${deltaXMiddle2} ${deltaYMiddle2} ` +
            `T ${svgWidth + deltaXEnd} ${deltaYEnd}`

          path.setAttribute('d', pathString)
        } else {
          path.setAttribute('d', '')
        }
      })
    }

    const areEqualItems = (item1: ItemCoordinatesInfo, item2: ItemCoordinatesInfo): boolean => {
      return item1.x1 === item2.x1 && item1.y1 === item2.y1
    }

    const getItemPosition = (
      item: ItemCoordinatesInfo,
      first: ItemCoordinatesInfo,
      last: ItemCoordinatesInfo,
    ): AlignEnum => {
      if (areEqualItems(item, first)) {
        return AlignEnum.START
      }
      if (areEqualItems(item, last)) {
        return AlignEnum.END
      }

      return AlignEnum.CENTER
    }

    const getButtonElement = (el: Element, className: string): Element => {
      return el.className === className ? el : getButtonElement(el.parentElement, className)
    }

    const animateItem = (svg: HTMLElement, expanded: boolean, width: number): number => {
      if (width === 60) {
        clearInterval(redrawOnClickInterval.value)
        redrawOnClickInterval.value = undefined
        drawSvgPathLines()
        return width
      }

      const animatedWidth = width + (expanded ? -10 : 5)
      svg.style.width = `${animatedWidth}px`
      drawSvgPathLines()
      return animatedWidth
    }

    const animateOnClick = (el: Element): void => {
      const labels = Array.from(document.getElementsByClassName(labelClass)) as HTMLElement[]
      const svgs = Array.from(document.getElementsByClassName(svgClass)) as HTMLElement[]

      const elementCoordinates = getCoordinatesInfo(el)

      const position = labels.findIndex(item => {
        const infoCoordinates = getCoordinatesInfo(item)
        return areEqualItems(infoCoordinates, elementCoordinates)
      })
      const svg = svgs[position]
      const nextItem = labels[position + 1]

      if (nextItem) {
        const expanded = props.item.expanded
        let width = animateItem(svg, expanded, expanded ? 180 : 0)

        if (redrawOnClickInterval.value) {
          clearInterval(redrawOnClickInterval.value)
        }
        redrawOnClickInterval.value = setInterval(() => {
          width = animateItem(svg, expanded, width)
        }, 10)
      }
    }

    const onClick = (el: Element): void => {
      if (!hasChildren(props.item)) {
        return
      }

      const buttonEl = getButtonElement(el, labelClass)
      animateOnClick(buttonEl)

      emit('onClick', props.item)
    }

    const onHover = (el: Element, hover: boolean): void => {
      if (labelClass !== el.className || hasChildren(props.item)) {
        return
      }

      const labels = Array.from(document.getElementsByClassName(labelClass)) as HTMLElement[]
      const firstLabelAttribute = getCoordinatesInfo(labels[0])
      const lastLabelAttribute = getCoordinatesInfo(labels[labels.length - 1])
      const elementAttribute = getCoordinatesInfo(el)

      hovered.value = hover

      if (redrawOnHoverInterval.value) {
        clearInterval(redrawOnHoverInterval.value)
      }
      redrawOnHoverInterval.value = setInterval(() => {
        drawSvgPathLines()
      }, 10)

      if (redrawOnHoverTimeout.value) {
        clearTimeout(redrawOnHoverTimeout.value)
      }
      redrawOnHoverTimeout.value = setTimeout(() => {
        clearInterval(redrawOnHoverInterval.value)
        redrawOnHoverInterval.value = undefined
        redrawOnHoverTimeout.value = undefined
      }, 550)

      emit(
        'onUpdateResizeDirection',
        getItemPosition(elementAttribute, firstLabelAttribute, lastLabelAttribute),
      )
    }

    onBeforeUnmount(() => {
      if (redrawOnHoverInterval.value) {
        clearInterval(redrawOnHoverInterval.value)
      }
      if (redrawOnHoverTimeout.value) {
        clearTimeout(redrawOnHoverTimeout.value)
      }
      if (redrawOnClickInterval.value) {
        clearTimeout(redrawOnClickInterval.value)
      }
    })

    onMounted(() => {
      if (props.isRoot) {
        nextTick(() => {
          drawSvgPathLines()
        })
      }
    })

    onUpdated(() => {
      if (props.isRoot) {
        nextTick(() => {
          drawSvgPathLines()
        })
      }
    })

    return {
      baseClass,
      labelClass,
      svgClass,
      pathClass,
      hasChildren,
      onHover,
      hovered,
      onClick,
      getExpandedIcon,
      emit,
      ColorEnum,
    }
  },
})
